import { pageErrorHandler } from '~/utils/page-error-handler';
import { autoinject, newInstance, PLATFORM, useView } from 'aurelia-framework';
import { ValidationController } from 'aurelia-validation';

import { CustomisationsApi } from '~/api/customisations-api';
import {
	BaseDialog,
	BaseDialogModel,
	BaseDialogModelDefaults,
} from '~/components/dialog/base-dialog';
import { SelectOption } from '~/components/field/common/select';

export interface CustomisationEditorSelectResult {
	formName: string;
	areaName: string;
	customisationName: string;
	functionName?: string;
}

export interface CustomisationEditorSelectDialogModel extends BaseDialogModel {
	/**
	 * This will make extra customisation areas (possibly on other forms) accessible from the dialog.
	 */
	extraItems?: Array<{ formName: string; areaName: string }>;
	defaultFunctionName?: string;
	areaFunctionNames?: { [areaName: string]: string };
}

interface AreaSelectOption extends SelectOption {
	formName: string;
}

@autoinject
@useView(PLATFORM.moduleName('./customisation-editor-select-dialog.html'))
export class CustomisationEditorSelectDialog extends BaseDialog<CustomisationEditorSelectDialogModel> {
	private customisationName: string = '';

	private areaNameValue: string = '';

	private areaNameOptions: AreaSelectOption[] = [];

	constructor(
		private readonly customisationsApi: CustomisationsApi,
		@newInstance()
		private readonly validationController?: ValidationController,
		...rest
	) {
		super(...rest);
	}

	private getResult(): CustomisationEditorSelectResult {
		const selectedOptionArray = this.areaNameOptions.filter(
			(areaNameOption) => areaNameOption.value === this.areaNameValue,
		);
		const formName = selectedOptionArray[0].formName;

		const functionName =
			this.model.areaFunctionNames &&
			this.model.areaFunctionNames[this.areaNameValue]
				? this.model.areaFunctionNames[this.areaNameValue]
				: this.model.defaultFunctionName;

		return {
			formName,
			areaName: this.areaNameValue,
			customisationName: this.customisationName,
			functionName,
		};
	}

	protected activate(
		model: CustomisationEditorSelectDialogModel,
		defaults?: any,
	): Promise<any> {
		const parentPromise = super.activate(model, defaults);
		const formName = this.customisationsApi.getLastCustomisationFormName();
		this.customisationName =
			this.customisationsApi.getLastCustomisationCustomisationName();
		const areaNames =
			this.customisationsApi.getLastCustomisationAreaNames();
		for (const areaName of areaNames) {
			this.areaNameOptions.push({
				displayName: areaName,
				value: areaName,
				formName,
			});
		}
		if (this.model.extraItems) {
			for (const extraItem of this.model.extraItems) {
				this.areaNameOptions.push({
					displayName: extraItem.areaName,
					value: extraItem.areaName,
					formName: extraItem.formName,
				});
			}
		}
		return Promise.all([parentPromise]).catch(pageErrorHandler);
	}
}
